import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, output, ViewChild } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, take } from 'rxjs';
import { BreakpointObserverService } from 'src/app/shared/services/breakpoint-observer/breakpoint-observer.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { CustomContextMenuItem } from '../../ui/custom-context-menu/custom-context-menu.component';
import { CustomContextMenuService } from '../../ui/custom-context-menu/custom-context-menu.service';
import { CallBlurSettingsComponent } from '../call-blur-settings/call-blur-settings.component';
import { CallDevicesComponent } from '../call-devices/call-devices.component';
import { CallInvitationComponent } from '../call-invitation/call-invitation.component';
import { ChimeService } from '../chime.service';
import { DialNumberComponent } from '../dial-number/dial-number.component';
import { EndCallComponent } from './end-call.component';

@Component({
  selector: 'app-call-controls',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, TranslateModule, MatBadgeModule],
  template: `
    <div class="call-controls-bar" [class.in-drawer]="isDrawer()" [@slideUp]>
      <!-- Media Controls -->
      <div class="control-group">
        @if (!isAudioLocked() || isHost()) {
          @if (micOn()) {
            <button class="btn-state-idle" mat-flat-button (click)="toggleMic.emit()">
              <mat-icon>mic</mat-icon>
            </button>
          } @else {
            <button class="btn-state-off" mat-flat-button (click)="toggleMic.emit()">
              <mat-icon>mic_off</mat-icon>
            </button>
          }
        }

        @if (!isCameraLocked() || isHost()) {
          @if (cameraActive()) {
            <button class="btn-state-idle" mat-flat-button (click)="toggleCamera.emit()">
              <mat-icon>videocam</mat-icon>
            </button>
          } @else {
            <button class="btn-state-off" mat-flat-button (click)="toggleCamera.emit()">
              <mat-icon>videocam_off</mat-icon>
            </button>
          }
        }

        @if (isScreenShareSupported && (!isScreenShareLocked() || isHost())) {
          @if (isScreenSharing()) {
            <button class="btn-state-active" mat-flat-button (click)="toggleScreenShare.emit()">
              <mat-icon>stop_screen_share</mat-icon>
            </button>
          } @else {
            <div #screenShareTrigger>
              <button class="btn-state-idle" mat-flat-button (click)="onScreenShareClick($event, screenShareTrigger)">
                <mat-icon>screen_share</mat-icon>
              </button>
            </div>
          }
        }
      </div>

      @if (hasMediaControls()) {
        <div class="divider"></div>
      }

      <!-- Utility Controls -->
      <div class="control-group">
        @if (!isChatLocked() || isHost()) {
          @if (chatActive()) {
            <button class="btn-state-active" mat-flat-button (click)="toggleChatPanel()">
              <mat-icon>chat_bubble_outline</mat-icon>
            </button>
          } @else {
            <button
              class="btn-state-idle"
              mat-flat-button
              (click)="toggleChatPanel()"
              [matBadge]="unreadCount()"
              [matBadgeHidden]="unreadCount() === 0"
              matBadgeColor="warn"
              matBadgeSize="medium"
            >
              <mat-icon>chat_bubble_outline</mat-icon>
            </button>
          }
        }

        <div #moreOptionsTrigger>
          <button
            class="btn-state-idle"
            mat-flat-button
            (click)="onOptionsClick($event, moreOptionsTrigger)"
            [matBadge]="waitingCount()"
            [matBadgeHidden]="waitingCount() === 0"
            matBadgeColor="warn"
            matBadgeSize="medium"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
        </div>
      </div>

      <div class="divider"></div>

      <!-- End Call -->
      <div #callEndTrigger>
        <button class="call-end-btn" mat-flat-button (click)="openEndCallConfirmation($event, callEndTrigger)">
          <mat-icon>call_end</mat-icon>
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./call-controls.component.scss'],
  animations: [
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translate(-50%, 100%)', opacity: 0 }),
        animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translate(-50%, 0)', opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translate(-50%, 100%)', opacity: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallControlsComponent {
  // --- Inputs ---
  isDrawer = input(false);
  micOn = input(false);
  cameraActive = input(false);
  isScreenSharing = input(false);
  chatActive = input(false);
  isRecording = input(false);
  isHost = input(false);
  participantCount = input(0);
  waitingCount = input(0);
  isVideoFxActive = input(false);
  callID = input.required<string>();

  // --- Outputs ---
  toggleMic = output<void>();
  toggleCamera = output<void>();
  toggleScreenShare = output<'content-only' | 'with-sidebar' | 'full' | void>();
  toggleChat = output<void>();
  leaveCall = output<void>();
  toggleRecording = output<void>();
  dialing = output<string>();
  toggleParticipants = output<void>();
  toggleVideoFx = output<void>();

  @ViewChild('moreOptionsTrigger') private moreOptionsTrigger!: ElementRef<HTMLDivElement>;

  private dialerSub: Subscription;
  private endCallSub: Subscription;
  private callInviteInputSub: Subscription;

  private dynamicOverlayService = inject(DynamicOverlayService);
  private contextMenuService = inject(CustomContextMenuService);
  private chimeService = inject(ChimeService);
  private breakpointObserver = inject(BreakpointObserverService);
  unreadCount = this.chimeService.unreadCount;
  isAudioLocked = this.chimeService.isAudioLocked;
  isCameraLocked = this.chimeService.isCameraLocked;
  isScreenShareLocked = this.chimeService.isScreenShareLocked;
  isChatLocked = this.chimeService.isChatLocked;
  isScreenShareSupported = typeof navigator !== 'undefined' && !!navigator?.mediaDevices && !!navigator?.mediaDevices?.getDisplayMedia;
  hasMediaControls = computed(() => {
    const isHost = this.isHost();
    const canAudio = !this.isAudioLocked() || isHost;
    const canCamera = !this.isCameraLocked() || isHost;
    const canScreenShare = this.isScreenShareSupported && (!this.isScreenShareLocked() || isHost);
    return canAudio || canCamera || canScreenShare;
  });

  menuItems: CustomContextMenuItem[] = [
    { id: 'recording', text: 'VideoCall.CallControls.StartRecording', icon: 'adaptive_audio_mic', func: () => this.toggleRecording.emit() },
    { id: 'phoneCall', text: 'VideoCall.CallControls.PhoneCall', icon: 'phone', func: () => this.openDialer() },
    { id: 'selectDevice', text: 'VideoCall.CallControls.SelectDevice', icon: 'settings_input_component', func: () => this.openDevicePicker() },
    { id: 'blurBackground', text: 'VideoCall.CallControls.BlurBackground', icon: 'blur_on', func: () => this.openBlurSettings() },
    { id: 'participantList', text: 'VideoCall.CallControls.ParticipantList', icon: 'group', func: () => this.toggleParticipants.emit() },
    { id: 'invitePeople', text: 'VideoCall.CallControls.InvitePeople', icon: 'person_add', func: () => this.openCallInvitation() },
    { id: 'whiteboard', text: 'VideoCall.CallControls.Whiteboard', icon: 'gesture', func: () => this.toggleWhiteboard() },
  ];

  constructor() {
    effect(() => {
      // Update the recording menu item when isRecording changes
      const isRec = this.isRecording();
      const recordingItem = this.menuItems.find((item) => item.id === 'recording');
      if (recordingItem) {
        recordingItem.text = isRec ? 'VideoCall.CallControls.StopRecording' : 'VideoCall.CallControls.StartRecording';
        recordingItem.icon = isRec ? 'stop_circle' : 'adaptive_audio_mic';
      }
    });

    effect(() => {
      // Update the video FX menu item when isVideoFxActive changes
      const blurOn = this.isVideoFxActive();
      const videoFxItem = this.menuItems.find((item) => item.id === 'blurBackground');
      if (videoFxItem) {
        videoFxItem.text = blurOn ? 'VideoCall.CallControls.DisableBlur' : 'VideoCall.CallControls.BlurBackground';
        videoFxItem.icon = blurOn ? 'blur_off' : 'blur_on';
      }

      if (this.cameraActive()) {
        if (videoFxItem) videoFxItem.disabled = false;
      } else {
        if (videoFxItem) videoFxItem.disabled = true;
      }
    });

    effect(() => {
      // Adjust menu items based on host status
      const isHost = this.isHost();
      if (!isHost) {
        this.menuItems = this.menuItems.filter((item) => !['recording', 'phoneCall', 'invitePeople'].includes(item.id));
      }
    });

    effect(() => {
      const waitCount = this.waitingCount();
      const participantItem = this.menuItems.find((item) => item.id === 'participantList');
      if (participantItem) {
        participantItem.text = 'VideoCall.CallControls.ParticipantList';
        participantItem.badge = waitCount > 0 ? waitCount : undefined;
      }
    });

    effect(() => {
      const active = this.chimeService.isGeminiLiveActive();
      const geminiItem = this.menuItems.find((item) => item.id === 'geminiLive');
      if (geminiItem) {
        geminiItem.text = active ? 'VideoCall.CallControls.StopGeminiLive' : 'VideoCall.CallControls.StartGeminiLive';
        geminiItem.icon = active ? 'auto_awesome' : 'auto_awesome_motion';
      }
    });
  }

  toggleChatPanel(): void {
    this.toggleChat.emit();
  }

  toggleWhiteboard(): void {
    this.chimeService.toggleWhiteboard();
  }

  toggleGeminiLive(): void {
    const callID = this.callID();
    this.chimeService.toggleGeminiLiveAssistant(callID);
  }

  onOptionsClick(event: MouseEvent, triggerElement: HTMLElement): void {
    event.stopPropagation();
    let items = this.menuItems;
    if (this.breakpointObserver.isSmallScreen() || this.breakpointObserver.isTabletScreen()) {
      items = items.filter((item) => item.id !== 'whiteboard');
    }
    this.contextMenuService.open(triggerElement, items).subscribe();
  }

  openDevicePicker(): void {
    const event = new MouseEvent('click');
    event.stopPropagation();

    const { ref: overlayRef } = this.dynamicOverlayService.open(this.moreOptionsTrigger, CallDevicesComponent);

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {});
  }

  openBlurSettings(): void {
    const event = new MouseEvent('click');
    event.stopPropagation();

    const { ref: overlayRef } = this.dynamicOverlayService.open(this.moreOptionsTrigger, CallBlurSettingsComponent);

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {});
  }

  openDialer(): void {
    const event = new MouseEvent('click');
    event.stopPropagation();
    this.dialerSub?.unsubscribe();

    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open(this.moreOptionsTrigger, DialNumberComponent);

    this.dialerSub = componentInstance.dial.subscribe((phoneNumber: string) => {
      this.dialing.emit(phoneNumber);
      this.dynamicOverlayService.close();
    });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {
        this.dialerSub?.unsubscribe();
      });
  }

  openEndCallConfirmation(event: MouseEvent, triggerElement: HTMLElement): void {
    event.stopPropagation();
    this.endCallSub?.unsubscribe();

    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open(triggerElement, EndCallComponent);

    this.endCallSub = componentInstance.confirm.subscribe(() => {
      this.leaveCall.emit();
      this.dynamicOverlayService.close();
    });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {
        this.endCallSub?.unsubscribe();
      });
  }

  openCallInvitation(): void {
    const event = new MouseEvent('click');
    event.stopPropagation();

    this.callInviteInputSub?.unsubscribe();
    const { ref: overlayRef } = this.dynamicOverlayService.open(this.moreOptionsTrigger, CallInvitationComponent, {
      data: {
        openedInOverlay: true,
        callID: this.callID(),
      },
    });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {
        this.callInviteInputSub?.unsubscribe();
      });
  }

  onScreenShareClick(event: MouseEvent, triggerElement: HTMLElement): void {
    event.stopPropagation();
    const items: CustomContextMenuItem[] = [
      {
        id: 'content-only',
        text: 'VideoCall.ScreenShare.ContentOnly',
        icon: 'crop_free',
        func: () => this.toggleScreenShare.emit('content-only'),
        onMouseEnter: () => this.highlightSelector('.content-card-body', true),
        onMouseLeave: () => this.highlightSelector('.content-card-body', false),
      },
      {
        id: 'with-sidebar',
        text: 'VideoCall.ScreenShare.WithLeftPanel',
        icon: 'grid_view',
        func: () => this.toggleScreenShare.emit('with-sidebar'),
        onMouseEnter: () => this.highlightSelector('.screenshare-crop-helper', true),
        onMouseLeave: () => this.highlightSelector('.screenshare-crop-helper', false),
      },
      {
        id: 'full',
        text: 'VideoCall.ScreenShare.Full',
        icon: 'screen_share',
        func: () => this.toggleScreenShare.emit('full'),
      },
    ];

    this.contextMenuService.open(triggerElement, items).subscribe({
      next: () => {
        this.highlightSelector('.content-card-body', false);
        this.highlightSelector('.screenshare-crop-helper', false);
      },
      error: () => {
        this.highlightSelector('.content-card-body', false);
        this.highlightSelector('.screenshare-crop-helper', false);
      },
      complete: () => {
        this.highlightSelector('.content-card-body', false);
        this.highlightSelector('.screenshare-crop-helper', false);
      },
    });
  }

  private highlightSelector(selector: string, highlight: boolean): void {
    const element = document.querySelector(selector);
    if (element) {
      if (highlight) {
        element.classList.add('screenshare-target-highlight');
      } else {
        element.classList.remove('screenshare-target-highlight');
      }
    }
  }
}
